import { useEffect, useMemo, useState } from 'react'
import * as Sentry from '@sentry/react'
import { Save, X, Copy, Loader } from 'lucide-react'
import { useAuth } from '../../context/AuthContext'
import { useToast } from '../../context/ToastContext'
import { getProducts, getProductsAsync } from '../../data/products'
import { getDistributionPoints } from '../../data/bakeryData'
import {
  refreshBakeryDistributionPrices,
  setSiteProductPrice,
  removeSiteProductPrice,
  getSitePriceMap,
  isSiteOverride,
  copySitePrices,
} from '../../lib/bakeryPricingStore'
import { getProductPrice } from '../../lib/productPricing'

const DEPT = 'bakery'

function fmtR(v) {
  if (v == null) return '—'
  const n = Number(v)
  if (!Number.isFinite(n)) return '—'
  return `R${n.toFixed(2)}`
}

function StatusPill({ sitePrice, basePrice }) {
  if (sitePrice == null || basePrice == null) return <span className="text-gray-500">— Base</span>
  if (Math.abs(sitePrice - basePrice) <= 0.01) {
    return <span className="text-gray-500">— Base</span>
  }
  if (sitePrice < basePrice) {
    return <span className="text-amber-400">🟡 Custom ↓</span>
  }
  return <span className="text-blue-400">🔵 Custom ↑</span>
}

export default function DistributionPricesTab() {
  const { user } = useAuth()
  const { addToast } = useToast()
  const adminEmail = user?.email || 'unknown'

  const [sites, setSites] = useState(() => getDistributionPoints())
  const [selectedSiteId, setSelectedSiteId] = useState(sites[0]?.id || '')
  const [products, setProducts] = useState(() => getProducts(DEPT))
  const [loading, setLoading] = useState(true)
  const [priceRefreshKey, setPriceRefreshKey] = useState(0)
  const [editing, setEditing] = useState(null) // productCode being edited
  const [editValue, setEditValue] = useState('')
  const [saving, setSaving] = useState(false)
  const [showCopy, setShowCopy] = useState(false)
  const [copyFromId, setCopyFromId] = useState('')

  // Initial load: hydrate overrides from Firestore + ensure products async.
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        await Promise.all([
          refreshBakeryDistributionPrices(DEPT),
          getProductsAsync(DEPT).then(p => { if (!cancelled) setProducts(p) }),
        ])
      } catch (err) {
        Sentry.captureException(err, { extra: { fn: 'DistributionPricesTab.init' } })
      } finally {
        if (!cancelled) { setLoading(false); setPriceRefreshKey(k => k + 1) }
      }
    })()
    return () => { cancelled = true }
  }, [])

  const selectedSite = sites.find(s => s.id === selectedSiteId) || null

  const priceMap = useMemo(
    () => selectedSiteId ? getSitePriceMap(DEPT, selectedSiteId, products) : {},
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedSiteId, products, priceRefreshKey]
  )

  // Group products by category for a cleaner table.
  const rows = useMemo(() => {
    return products
      .filter(p => !p.deleted)
      .map(p => {
        const basePrice = Number(getProductPrice(p, DEPT)) || 0
        const sitePrice = priceMap[p.code] ?? basePrice
        const hasOverride = selectedSiteId && isSiteOverride(DEPT, selectedSiteId, p.code)
        return { product: p, basePrice, sitePrice, hasOverride }
      })
      .sort((a, b) => {
        const c = String(a.product.category || '').localeCompare(String(b.product.category || ''))
        return c !== 0 ? c : String(a.product.name).localeCompare(String(b.product.name))
      })
  }, [products, priceMap, selectedSiteId, priceRefreshKey])

  const overrideCount = rows.filter(r => r.hasOverride).length
  const baseCount = rows.length - overrideCount

  // ─── Edit handlers ─────────────────────────────────────────────────

  const startEdit = (row) => {
    setEditing(row.product.code)
    setEditValue(String(row.hasOverride ? row.sitePrice : row.basePrice))
  }
  const cancelEdit = () => { setEditing(null); setEditValue('') }

  const saveEdit = async (row) => {
    const trimmed = String(editValue).trim()
    const n = Number(trimmed)
    if (trimmed === '' || !Number.isFinite(n) || n < 0) {
      addToast('Enter a valid non-negative price', 'error'); return
    }
    setSaving(true)
    try {
      await setSiteProductPrice(DEPT, selectedSiteId, selectedSite?.name || selectedSiteId, row.product.code, n, adminEmail)
      addToast(`${row.product.name} at ${selectedSite?.name || selectedSiteId}: ${fmtR(row.basePrice)} → ${fmtR(n)}`, 'success')
      cancelEdit()
      setPriceRefreshKey(k => k + 1)
    } catch (err) {
      addToast(`Save failed: ${err.message}`, 'error')
    } finally { setSaving(false) }
  }

  const onKey = (e, row) => {
    if (e.key === 'Enter') saveEdit(row)
    else if (e.key === 'Escape') cancelEdit()
  }

  const handleRemove = async (row) => {
    if (!window.confirm(`Remove custom price for ${row.product.name} at ${selectedSite?.name || selectedSiteId}? It will revert to the base price of ${fmtR(row.basePrice)}.`)) return
    try {
      await removeSiteProductPrice(DEPT, selectedSiteId, row.product.code)
      addToast(`${row.product.name} at ${selectedSite?.name || selectedSiteId} reverted to base ${fmtR(row.basePrice)}`, 'success')
      setPriceRefreshKey(k => k + 1)
    } catch (err) {
      addToast(`Remove failed: ${err.message}`, 'error')
    }
  }

  const handleCopy = async () => {
    if (!copyFromId || copyFromId === selectedSiteId) {
      addToast('Pick a different source site', 'error'); return
    }
    setSaving(true)
    try {
      const res = await copySitePrices(DEPT, copyFromId, selectedSiteId, selectedSite?.name || selectedSiteId, adminEmail)
      addToast(`Copied ${res.copied} override${res.copied === 1 ? '' : 's'} · Skipped ${res.skipped}`, 'success')
      setShowCopy(false)
      setCopyFromId('')
      setPriceRefreshKey(k => k + 1)
    } catch (err) {
      addToast(`Copy failed: ${err.message}`, 'error')
    } finally { setSaving(false) }
  }

  if (loading) {
    return (
      <div className="py-12 text-center text-gray-400">
        <Loader size={24} className="animate-spin mx-auto" />
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-white">Distribution Prices — Bakery</h2>
        <p className="text-xs text-gray-500">
          Set site-specific prices. Base prices from the Price Book are used when no override is set.
        </p>
      </div>

      {/* Site selector */}
      <div className="flex flex-wrap gap-1">
        {sites.map(s => (
          <button
            key={s.id}
            onClick={() => { setSelectedSiteId(s.id); cancelEdit() }}
            className={`px-3 py-1.5 rounded text-xs font-medium ${
              selectedSiteId === s.id ? 'bg-brand-700 text-white' : 'bg-gray-800 text-gray-400 hover:text-white'
            }`}
          >
            {s.shortName || s.name}
          </button>
        ))}
      </div>

      {/* Copy-from affordance */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-xs text-gray-400">
          {selectedSite?.name || '—'} · <span className="text-amber-400">{overrideCount} custom</span> · <span className="text-gray-500">{baseCount} base</span>
        </p>
        {!showCopy ? (
          <button
            onClick={() => setShowCopy(true)}
            className="flex items-center gap-1.5 text-xs bg-gray-800 hover:bg-gray-700 border border-gray-700 text-gray-200 px-3 py-1.5 rounded"
          >
            <Copy size={12} /> Copy prices from another site
          </button>
        ) : (
          <div className="flex items-center gap-2">
            <select
              value={copyFromId}
              onChange={e => setCopyFromId(e.target.value)}
              className="bg-gray-800 border border-gray-700 rounded px-2 py-1 text-xs text-gray-200"
            >
              <option value="">— Pick source site —</option>
              {sites.filter(s => s.id !== selectedSiteId).map(s => (
                <option key={s.id} value={s.id}>{s.shortName || s.name}</option>
              ))}
            </select>
            <button
              onClick={handleCopy}
              disabled={!copyFromId || saving}
              className="bg-brand-600 hover:bg-brand-500 disabled:opacity-50 text-white text-xs px-3 py-1 rounded"
            >
              Copy
            </button>
            <button onClick={() => { setShowCopy(false); setCopyFromId('') }} className="text-gray-400 text-xs hover:text-white">Cancel</button>
          </div>
        )}
      </div>

      {/* Price table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-gray-400 text-xs border-b border-gray-800">
              <th className="text-left py-2 px-2">Product</th>
              <th className="text-left py-2 px-2">Category</th>
              <th className="text-right py-2 px-2">Base Price</th>
              <th className="text-right py-2 px-2">Site Price</th>
              <th className="text-left py-2 px-2">Status</th>
              <th className="text-center py-2 px-2 w-32">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(row => {
              const isEditingThis = editing === row.product.code
              return (
                <tr key={row.product.code} className="border-b border-gray-800/50 hover:bg-gray-800/30">
                  <td className="py-2 px-2">
                    <div className="text-white text-xs font-medium">{row.product.name}</div>
                    <div className="text-gray-500 font-mono text-[10px]">{row.product.code}</div>
                  </td>
                  <td className="py-2 px-2 capitalize text-gray-400 text-xs">{row.product.category}</td>
                  <td className="py-2 px-2 text-right text-gray-400">{fmtR(row.basePrice)}</td>
                  <td className="py-2 px-2 text-right">
                    {isEditingThis ? (
                      <input
                        autoFocus
                        type="number" min="0" step="0.5"
                        value={editValue}
                        onChange={e => setEditValue(e.target.value)}
                        onKeyDown={e => onKey(e, row)}
                        className="bg-gray-800 border border-gray-600 rounded px-2 py-1 text-xs text-white w-24 text-right"
                      />
                    ) : (
                      <span className={row.hasOverride ? 'text-white font-medium' : 'text-gray-400'}>
                        {fmtR(row.sitePrice)}
                      </span>
                    )}
                  </td>
                  <td className="py-2 px-2 text-xs">
                    <StatusPill sitePrice={row.sitePrice} basePrice={row.basePrice} />
                  </td>
                  <td className="py-2 px-2 text-center">
                    {isEditingThis ? (
                      <div className="flex items-center justify-center gap-1">
                        <button onClick={() => saveEdit(row)} disabled={saving} className="text-green-400 hover:text-green-300 disabled:opacity-50">
                          <Save size={12} />
                        </button>
                        <button onClick={cancelEdit} disabled={saving} className="text-gray-500 hover:text-gray-300 disabled:opacity-50">
                          <X size={12} />
                        </button>
                      </div>
                    ) : row.hasOverride ? (
                      <div className="flex items-center justify-center gap-2">
                        <button onClick={() => startEdit(row)} className="text-[10px] px-2 py-0.5 rounded bg-brand-900/40 text-brand-300 hover:bg-brand-900">Edit</button>
                        <button onClick={() => handleRemove(row)} className="text-[10px] px-2 py-0.5 rounded bg-red-900/40 text-red-300 hover:bg-red-900">Remove</button>
                      </div>
                    ) : (
                      <button onClick={() => startEdit(row)} className="text-[10px] px-2 py-0.5 rounded bg-gray-800 text-gray-300 hover:bg-gray-700 border border-gray-700">Set Price</button>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
        {rows.length === 0 && (
          <p className="text-center py-8 text-sm text-gray-500">No bakery products loaded.</p>
        )}
      </div>
    </div>
  )
}
